import { useLocationAlarmUpdates } from '@hakimo-ui/hakimo/data-access';
import {
  DataVersion,
  IncidentProduct,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import {
  DispatchTimelineEditor,
  DispatchTimelineEditorHandle,
  DispatchTimelineRowWide,
  SnapshotUploader,
} from '@hakimo-ui/hakimo/feature-shared';
import { toast } from '@hakimo-ui/hakimo/util';
import { Modal } from '@hakimo-ui/shared/ui-base';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Panel } from './Panel';
import { EDITABLE_UPDATE_TYPES } from './util';
import { useEffect, useRef, useState } from 'react';

interface Props {
  alarmId: number | string;
  product: IncidentProduct;
  timezone: string | null;
  alarmStartTimeUtc: string | null;
  dataVersion?: DataVersion;
  onTimelineChanged: () => void;
}

export function IncidentTimelineSection({
  alarmId,
  product,
  timezone,
  alarmStartTimeUtc,
  dataVersion,
  onTimelineChanged,
}: Props) {
  const editorRef = useRef<DispatchTimelineEditorHandle>(null);
  const [editing, setEditing] = useState(false);
  const [addingEvent, setAddingEvent] = useState(false);
  const wasEditing = useRef(false);

  const monitoringProduct =
    product === 'scan'
      ? UnifiedMonitoringProduct.SCAN
      : UnifiedMonitoringProduct.REMOTE_GUARDING;

  const { data: alarmUpdates } = useLocationAlarmUpdates(
    String(alarmId),
    0,
    alarmStartTimeUtc ?? '',
    monitoringProduct,
    undefined,
    true,
    dataVersion
  );

  const events = (alarmUpdates?.items ?? []).filter((update) =>
    EDITABLE_UPDATE_TYPES.has(update.update_type as string)
  );

  useEffect(() => {
    if (wasEditing.current && !editing) {
      editorRef.current
        ?.flushUpdates(true)
        .then((sent) => sent && onTimelineChanged())
        .catch(() =>
          toast('Could not save timeline change', { type: 'error' })
        );
    }
    wasEditing.current = editing;
  }, [editing, onTimelineChanged]);

  return (
    <Panel>
      <DispatchTimelineEditor
        ref={editorRef}
        events={events}
        timezone={timezone ?? undefined}
        alarmId={String(alarmId)}
        product={monitoringProduct}
        dataVersion={dataVersion}
        onEditingChange={setEditing}
        Row={DispatchTimelineRowWide}
        scrollable={false}
        titleClassName="text-lg"
        emptyState={
          <p className="text-onlight-text-2 dark:text-ondark-text-2 py-4 text-sm">
            No timeline events yet.
          </p>
        }
        action={
          <button
            type="button"
            onClick={() => setAddingEvent(true)}
            aria-label="Add event"
            className="text-onlight-text-2 dark:text-ondark-text-2 dark:hover:bg-ondark-bg-3 hover:bg-onlight-bg-2 border-onlight-line-2 dark:border-ondark-line-2 shrink-0 rounded-md border p-1"
          >
            <PlusIcon className="h-5 w-5" />
          </button>
        }
      />
      <Modal
        open={addingEvent}
        onClose={() => setAddingEvent(false)}
        title="Add Event"
      >
        <div className="w-[26rem] max-w-full p-4">
          <SnapshotUploader
            locationAlarmId={String(alarmId)}
            product={monitoringProduct}
            timezone={timezone ?? undefined}
            dataVersion={dataVersion}
            showTitle={false}
            onUploadSuccess={() => {
              setAddingEvent(false);
              onTimelineChanged();
            }}
          />
        </div>
      </Modal>
    </Panel>
  );
}

export default IncidentTimelineSection;
